import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import styles from "./Home.module.css";
import { useSelector } from "react-redux";

export default function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [leagueInfo, setLeagueInfo] = useState({ name: "", season: "" });
  const [loading, setLoading] = useState(true);

  const token = useSelector((state) => state.auth.token);

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const response = await axios.get(
          "https://admin.osleague.online/api/fixtures/active-rounds",
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        const matchesFromApi = response.data.data;

        if (matchesFromApi.length > 0) {
          setLeagueInfo({
            name: matchesFromApi[0].league.name,
            season: matchesFromApi[0].league.season,
          });
        }

        const formatDate = (dateString) => {
          const options = {
            weekday: "short",
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          };
          return new Date(dateString)
            .toLocaleDateString("en-GB", options)
            .replace(",", " ·");
        };

        const formattedData = matchesFromApi.map((match) => ({
          id: match.id,
          date: formatDate(match.match_date),
          status: match.status,
          league: match.league.name,
          leagueBadge: match.league.logo ?? null,
          home: {
            name: match.home_team.name,
            logo: match.home_team.logo,
            score: match.home_team.score,
          },
          away: {
            name: match.away_team.name,
            logo: match.away_team.logo,
            score: match.away_team.score,
          },
          canPredict: match.can_predict,
          prediction: match.prediction,
        }));
        setUpcomingMatches(formattedData);
      } catch (error) {
        console.error("Error loading fixtures:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [token]);

  const handlePredictNow = (match) => {
    if (match.canPredict) {
      navigate("/dashboard/predictions", { state: { match } });
    } else {
      console.log("التوقع مغلق لهذا المباراة");
    }
  };

  if (loading)
    return <div className={styles.loader}>{t("common.loading")}</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            {leagueInfo.name || t("home.matches")}
          </h1>
          <h4 className={styles.subtitle}>{t("home.subtitle")}</h4>
        </div>
        <div className={styles.liveBadge}>
          <span className={styles.liveDot} />
          {leagueInfo.season} {t("home.liveSeason")}
        </div>
      </div>

      <div className="container">
        <div className="row g-3">
          {upcomingMatches.map((match) => (
            <div key={match.id} className="col-12 col-md-6 col-lg-4">
              <div className={styles.card}>
                <div className={styles.matchup}>
                  <div className={styles.teamBlock}>
                    <div className={styles.teamLogo}>
                      <img
                        src={match.home.logo}
                        alt={match.home.name}
                        className={styles.teamImg}
                      />
                    </div>
                    <span className={styles.teamName}>{match.home.name}</span>
                  </div>

                  <div className={styles.vsBlock}>
                    {match.status === "finished" ? (
                      <div className={styles.scoreContainer}>
                        <span className={styles.scoreText}>
                          {match.home.score ?? 0} - {match.away.score ?? 0}
                        </span>
                        <span className={styles.finishedLabel}>
                          {t("finished")}
                        </span>
                      </div>
                    ) : (
                      <>
                        <span className={styles.vs}>VS</span>
                        <span className={styles.matchDate}>{match.date}</span>
                      </>
                    )}
                  </div>

                  <div className={styles.teamBlock}>
                    <div className={styles.teamLogo}>
                      <img
                        src={match.away.logo}
                        alt={match.away.name}
                        className={styles.teamImg}
                      />
                    </div>
                    <span className={styles.teamName}>{match.away.name}</span>
                  </div>
                </div>

                <button
                  className={`${styles.predictBtn} ${!match.canPredict ? styles.disabledBtn : ""}`}
                  onClick={() => handlePredictNow(match)}
                  disabled={!match.canPredict}
                >
                  {match.prediction
                    ? t("home.editPredict")
                    : t("home.predictNow")}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
